/**
 * =========================================================
 * Social Media Hub - Premium Dashboard Navbar
 * =========================================================
 *
 * Modern, sleek navbar with premium visual design,
 * smooth animations, and polished user experience.
 */

import { useState, useEffect } from "react";

// react-router components
import { useLocation, Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";

// Authentication
import { useAuth } from "context/AuthContext";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const { logout } = useAuth();
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleUserMenuOpen = (event) => setUserMenu(event.currentTarget);
  const handleUserMenuClose = () => setUserMenu(false);
  const handleLogout = () => {
    logout();
    setUserMenu(false);
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{
        mt: 2,
        "& .MuiMenu-paper": {
          minWidth: 280,
          borderRadius: "16px",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          p: 1,
        },
        "& .MuiMenuItem-root": {
          borderRadius: "12px",
          mx: 0.5,
          mb: 0.5,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(99, 102, 241, 0.08)",
          },
        },
      }}
    >
      <NotificationItem
        icon={
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: "white", fontSize: "1.1rem" }}>email</Icon>
          </Box>
        }
        title="Check new messages"
      />
      <NotificationItem
        icon={
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: "white", fontSize: "1.1rem" }}>podcasts</Icon>
          </Box>
        }
        title="Manage Podcast sessions"
      />
      <NotificationItem
        icon={
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon sx={{ color: "white", fontSize: "1.1rem" }}>shopping_cart</Icon>
          </Box>
        }
        title="Payment successfully completed"
      />
    </Menu>
  );

  // Render the user menu
  const renderUserMenu = () => (
    <Menu
      anchorEl={userMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      open={Boolean(userMenu)}
      onClose={handleUserMenuClose}
      sx={{
        mt: 2,
        "& .MuiMenu-paper": {
          minWidth: 200,
          borderRadius: "16px",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.12)",
          border: "1px solid rgba(0, 0, 0, 0.05)",
          p: 1,
        },
        "& .MuiMenuItem-root": {
          borderRadius: "12px",
          mx: 0.5,
          mb: 0.5,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(239, 68, 68, 0.08)",
          },
        },
      }}
    >
      <MenuItem onClick={handleLogout}>
        <MDBox display="flex" alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: "rgba(239, 68, 68, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1.5,
            }}
          >
            <Icon sx={{ color: "error.main", fontSize: "1.1rem" }}>logout</Icon>
          </Box>
          <MDTypography variant="button" fontWeight="medium" color="error">
            Logout
          </MDTypography>
        </MDBox>
      </MenuItem>
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"} display="flex" alignItems="center" gap={0.5}>
              {/* Menu button - first and prominent on mobile */}
              <Tooltip title={miniSidenav ? "Expand menu" : "Collapse menu"} arrow>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={{
                    ...navbarMobileMenu,
                    minWidth: "44px",
                    minHeight: "44px",
                    touchAction: "manipulation",
                    mr: { xs: 0.5, md: 0 },
                    backgroundColor: { xs: "rgba(99, 102, 241, 0.08)", md: "transparent" },
                    border: { xs: "1px solid rgba(99, 102, 241, 0.15)", md: "none" },
                    borderRadius: "12px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: {
                        xs: "rgba(99, 102, 241, 0.15)",
                        md: "rgba(0, 0, 0, 0.04)",
                      },
                      transform: "scale(1.02)",
                    },
                  }}
                  onClick={handleMiniSidenav}
                >
                  <Icon
                    sx={{
                      ...iconsStyle,
                      color: { xs: "#6366f1 !important", md: "inherit" },
                      transition: "transform 0.3s ease",
                      transform: miniSidenav ? "rotate(0deg)" : "rotate(180deg)",
                    }}
                    fontSize="medium"
                  >
                    menu_open
                  </Icon>
                </IconButton>
              </Tooltip>

              {/* User Avatar Button */}
              <Tooltip title="Account" arrow>
                <IconButton
                  sx={{
                    ...navbarIconButton,
                    minWidth: "44px",
                    minHeight: "44px",
                    touchAction: "manipulation",
                    borderRadius: "12px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      transform: "scale(1.02)",
                    },
                  }}
                  size="small"
                  disableRipple
                  onClick={handleUserMenuOpen}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  >
                    U
                  </Avatar>
                </IconButton>
              </Tooltip>

              {/* Settings Button */}
              <Tooltip title="Settings" arrow>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={{
                    ...navbarIconButton,
                    minWidth: "44px",
                    minHeight: "44px",
                    touchAction: "manipulation",
                    borderRadius: "12px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      transform: "scale(1.02)",
                    },
                  }}
                  onClick={handleConfiguratorOpen}
                >
                  <Icon sx={iconsStyle}>settings</Icon>
                </IconButton>
              </Tooltip>

              {/* Notifications Button */}
              <Tooltip title="Notifications" arrow>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={{
                    ...navbarIconButton,
                    minWidth: "44px",
                    minHeight: "44px",
                    touchAction: "manipulation",
                    borderRadius: "12px",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                      transform: "scale(1.02)",
                    },
                  }}
                  aria-controls="notification-menu"
                  aria-haspopup="true"
                  variant="contained"
                  onClick={handleOpenMenu}
                >
                  <Icon sx={iconsStyle}>notifications</Icon>
                </IconButton>
              </Tooltip>

              {renderMenu()}
              {renderUserMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
